import {
  defineComponent, ref, watch, PropType, cloneVNode,
} from 'vue'
import type { Options as OffsetOptions } from '@floating-ui/core/src/middleware/offset'
import { Text } from '../Text'
import { Icon } from '../Icon'
import { Button, ButtonContent } from '../Button'
import { Space } from '../Space'
import { Popover } from '../Popover'
import { Presets, Close } from '../../icons'
import { Placement, PortalType, ButtonType } from '../../enums'

export default defineComponent({
  inheritAttrs: false,
  props: {
    placement: {
      type: String as PropType<Placement>,
      default: 'top',
    },
    offset: {
      type: [Number, Object, Function] as PropType<OffsetOptions>,
      default: undefined,
    },
    arrow: {
      type: Boolean,
      default: false,
    },
    type: {
      type: String as PropType<PortalType>,
      default: undefined,
    },
    title: {
      type: String,
      default: undefined,
    },
    description: {
      type: String,
      default: undefined,
    },
    size: {
      type: [String, Number] as PropType<string | number>,
      default: undefined,
    },
    closeable: {
      type: Boolean,
      default: true,
    },
    withFooter: {
      type: Boolean,
      default: true,
    },
    visible: {
      type: Boolean,
      default: false,
    },
    outsideCloseable: {
      type: Boolean,
      default: false,
    },
    withConfirm: {
      type: Boolean,
      default: true,
    },
    confirmText: {
      type: String,
      default: '确定',
    },
    confirmType: {
      type: String as PropType<ButtonType>,
      default: 'primary',
    },
    confirmButtonProps: {
      type: Object as PropType<ButtonContent>,
      default: undefined,
    },
    withCancel: {
      type: Boolean,
      default: true,
    },
    cancelText: {
      type: String,
      default: '取消',
    },
    cancelType: {
      type: String as PropType<ButtonType>,
      default: 'light',
    },
    cancelButtonProps: {
      type: Object as PropType<ButtonContent>,
      default: undefined,
    },
  },
  emits: {
    'update:visible'(visible: boolean) {
      return visible !== undefined
    },
    confirm(e: MouseEvent) {
      return e !== undefined
    },
    cancel(e: MouseEvent) {
      return e !== undefined
    },
  },
  setup(props, { slots, attrs, emit }) {
    const visible = ref(props.visible)

    watch(
      () => props.visible,
      v => {
        visible.value = v
      },
    )

    watch(
      visible,
      v => emit('update:visible', v),
    )

    const popconfirm = ref<HTMLElement>()

    function popconfirmClose(event: MouseEvent) {
      if (props.outsideCloseable) {
        if (!event.composedPath().includes(popconfirm.value)) {
          visible.value = false
        }
      }
    }

    return () => {
      let icon

      switch (props.type) {
        case 'info':
          icon = <Presets.Info/>
          break
        case 'success':
          icon = <Presets.Success/>
          break
        case 'warning':
          icon = <Presets.Warning/>
          break
        case 'danger':
          icon = <Presets.Danger/>
          break
        default:
          break
      }

      const withHeader = !!(icon || slots.title || props.title || props.closeable)

      const content = cloneVNode(
        <div
          ref={popconfirm}
          class={
            [
              'd-popconfirm',
              'd-popconfirm-default',
              !withHeader && 'd-popconfirm-no-header',
              !props.withFooter && 'd-popconfirm-no-footer',
              attrs.class,
            ]
          }
          style={
            [
              props.size !== undefined
                ? {
                  width: typeof props.size === 'number'
                    ? `${props.size}px`
                    : props.size,
                }
                : undefined,
              attrs.style,
            ]
          }
        >
          {
            withHeader && <Space class={'d-popconfirm-header'} size={'small'}>
              {
                icon && <Icon
                  color={props.type}
                  size={'large'}
                >
                  {icon}
                </Icon>
              }
              {
                slots.title?.() || <Text
                  type={'h6'}
                  bold
                >
                  {props.title}
                </Text>
              }
              {
                props.closeable && <Icon
                  class={
                    [
                      'd-popconfirm-close',
                      'd-clickable',
                    ]
                  }
                  color={'current'}
                  icon={Close}
                  {
                    ...{
                      onClick() {
                        visible.value = false
                      },
                    }
                  }
                />
              }
            </Space>
          }
          <div class={'d-popconfirm-content'}>
            {slots.description?.() || <Text>{props.description}</Text>}
          </div>
          {
            props.withFooter && <div class={'d-popconfirm-footer'}>
              {
                slots.footer?.() || <Space block>
                  <div class={'d-popconfirm-footer-space-left'}/>
                  {
                    props.withCancel && <Button
                      type={props.cancelType}
                      {
                        ...{
                          onClick(e: MouseEvent) {
                            emit('cancel', e)
                          },
                        }
                      }
                      {...props.cancelButtonProps}
                    >
                      {props.cancelText}
                    </Button>
                  }
                  {
                    props.withConfirm && <Button
                      type={props.confirmType}
                      {
                        ...{
                          onClick(e: MouseEvent) {
                            emit('confirm', e)
                          },
                        }
                      }
                      {...props.confirmButtonProps}
                    >
                      {props.confirmText}
                    </Button>
                  }
                </Space>
              }
            </div>
          }
        </div>,
        {
          onVnodeMounted() {
            setTimeout(
              () => document.addEventListener('click', popconfirmClose),
              0,
            )
          },
          onVnodeBeforeUnmount() {
            document.removeEventListener('click', popconfirmClose)
          },
        },
      )

      return <Popover
        placement={props.placement}
        offset={props.offset}
        arrow={props.arrow}
        trigger={'manual'}
        visible={props.visible}
        {...attrs}
        v-slots={{
          default: slots.default,
          content: () => content,
        }}
      />
    }
  },
})
